import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatError } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { dsDtos, webDtos } from '@vectronic/openApi-admin';
import { SchedulePreview, ScheduleTimpespanPreview, utcMoment } from '@vectronic/util';
import { filter } from 'rxjs';
import { CameraScheduleService } from '../../../services/camera-schedule.service';
import { CalendarTimespanComponent } from '../../calendar-timespan/calendar-timespan.component';

@Component({
  selector: 'rc-camera-calendar-day-view',
  templateUrl: './camera-calendar-day-view.component.html',
  styleUrls: ['./camera-calendar-day-view.component.scss'],
  imports: [
    MatIconButton,
    MatTooltip,
    MatIcon,
    NgTemplateOutlet,
    MatButton,
    MatError,
    DatePipe,
    CalendarTimespanComponent,
  ],
})
export class CameraCalendarDayViewComponent {
  private cameraScheduleService = inject(CameraScheduleService);
  selectedDate = input<Date | null>();

  dayChange = output<Date>();
  back = output();

  schedulePreview = signal<SchedulePreview[]>(undefined);
  schedulePreviewOfPage = signal<SchedulePreview[]>(undefined);
  scheduleTimespanPreview = signal<ScheduleTimpespanPreview[]>(undefined);
  rulesArray = signal<webDtos.ScheduleRule[]>(undefined);
  hasNonTimerRules = signal<boolean>(false);

  nonTimerTriggerLabels = computed(() => {
    const timespans = this.scheduleTimespanPreview();
    if (!timespans?.length) return [];
    return [...new Set(timespans.map((t) => t.triggerLabel))];
  });

  page = 1;
  private readonly PAGE_SIZE = 20;

  constructor() {
    toObservable(this.selectedDate)
      .pipe(
        takeUntilDestroyed(),
        filter((v) => v != null),
      )
      .subscribe(() => this.updateSchedulesInDay());
    this.cameraScheduleService.rules$.pipe(takeUntilDestroyed()).subscribe((rules) => {
      this.rulesArray.set(Object.values(rules));
      this.updateSchedulesInDay();
    });
  }

  updateSchedulesInDay() {
    const schedules: SchedulePreview[] = [];
    const timespans: ScheduleTimpespanPreview[] = [];
    let nonTimer = false;
    this.rulesArray()?.forEach((rule) => {
      const cameraRule = rule as dsDtos.CameraScheduleRule;
      if (this.cameraScheduleService.isNonTimerTrigger(cameraRule)) {
        nonTimer = true;
        timespans.push(
          ...this.cameraScheduleService.timespanInDay(
            cameraRule,
            utcMoment(this.selectedDate(), true),
          ),
        );
      } else {
        schedules.push(
          ...(this.cameraScheduleService?.schedulesInDay(
            cameraRule,
            utcMoment(this.selectedDate(), true),
          ) ?? []),
        );
      }
    });
    this.hasNonTimerRules.set(nonTimer);
    this.schedulePreview.set(schedules?.sort((a, b) => a.millis - b.millis));
    this.scheduleTimespanPreview.set(timespans);
    this.page = 1;
    this.updatePageSlice();
  }

  private updatePageSlice() {
    const list = this.schedulePreview() ?? [];
    const start = (this.page - 1) * this.PAGE_SIZE;
    const end = this.page * this.PAGE_SIZE;
    this.schedulePreviewOfPage.set(list.slice(start, end));
  }

  private maxPage(): number {
    const len = this.schedulePreview()?.length ?? 0;
    return Math.max(1, Math.ceil(len / this.PAGE_SIZE));
  }

  schedulesToString(schedules: number[]): string {
    return schedules.map((schedule) => utcMoment(schedule, true).format('HH:mm')).join(', ');
  }

  millisToString(millis: number) {
    if (!millis) {
      return '';
    }
    return utcMoment(millis, true).format('HH:mm');
  }

  onClickBack() {
    this.back.emit();
  }

  onClickPrevious() {
    this.dayChange.emit(utcMoment(this.selectedDate(), true).add('day', -1).toDate());
  }

  onClickNext() {
    this.dayChange.emit(utcMoment(this.selectedDate(), true).add('day', 1).toDate());
  }

  nextPage() {
    this.page = Math.min(this.page + 1, this.maxPage());
    this.updatePageSlice();
  }

  previousPage() {
    this.page = Math.max(1, this.page - 1);
    this.updatePageSlice();
  }

  getTooltip(rule: dsDtos.CameraScheduleRule) {
    if (!rule) return undefined;
    if (rule.cameraScheduleRuleType === 'VIDEO') return `Video ${rule.size}s`;
    else return `Photo ${rule.size} count`;
  }
}
